import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardListIcon, CopyIcon, ExternalLinkIcon, LinkIcon, PencilIcon, PlusIcon, PowerIcon, Trash2Icon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { createForm, deleteForm, duplicateForm, setFormStatus } from '../../services/formService';
import { formatDate } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { FloatingMenu } from '../../components/shared/FloatingMenu';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Tag } from '../../components/ui/Tag';
import { toast } from '../../components/ui/Toast';
import { copyToClipboard, formUrl } from '../../utils/clipboard';
export function Forms() {
  const navigate = useNavigate();
  const {
    organizationId,
    ready,
    forms,
    leads,
    landings
  } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  async function handleCreate() {
    // Igual que en landings: no se escribe hasta tener la organización lista.
    if (!newName.trim() || !ready || saving) return;
    setSaving(true);
    try {
      const form = await createForm(organizationId, newName.trim());
      setCreating(false);
      setNewName('');
      navigate(`/app/formularios/${form.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No hemos podido crear el formulario.');
    } finally {
      setSaving(false);
    }
  }
  return <>
      <PageHeader title="Formularios" description="Crea las preguntas que verán tus potenciales clientes." actions={<Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1286" data-mp="2yj2c7ng8c2p3" />} onClick={() => setCreating(true)} data-id="element-1285" data-mp="2yjk467vuzur7">
            Crear formulario
          </Button>} data-id="element-1284" data-mp="2fm0f5ieromyk" />

      {forms.length === 0 ? <EmptyState icon={<ClipboardListIcon className="h-5 w-5" data-id="element-1288" data-mp="2pr0e3vtmuyhz" />} title="Todavía no tienes formularios" description="Crea tu primer formulario y asócialo a una landing para empezar a recibir leads." action={<Button onClick={() => setCreating(true)} data-id="element-1289" data-mp="2fsaspt2j2vwz">Crear formulario</Button>} data-id="element-1287" data-mp="2pqgu89kfa01b" /> : <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-1290" data-mp="2fu3zr9h1zi5i">
          {forms.map(form => {
        const responses = leads.filter(l => l.origin.formId === form.id).length;
        const linked = landings.filter(l => l.formId === form.id);
        return <li key={form.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1291" data-mp="2atzkfmrvstgv">
                <div className="flex items-start justify-between gap-3" data-id="element-1292" data-mp="50hq6b6i0b3z">
                  <div className="min-w-0" data-id="element-1293" data-mp="91uxkpp8omd0">
                    <Link to={`/app/formularios/${form.id}`} className="block truncate text-base font-semibold text-foreground hover:text-primary" data-id="element-1294" data-mp="8zh2whyh1c5z">
                      {form.name}
                    </Link>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground" data-id="element-1295" data-mp="1pg7ehyq44lhf">
                      {form.description || 'Sin descripción'}
                    </p>
                  </div>
                  <FloatingMenu align="right" label="Acciones del formulario" trigger={<span className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-surface" data-id="element-1297" data-mp="2d6a3g5yqdbbt">
                        ⋯
                      </span>} items={[{
              label: 'Editar',
              icon: <PencilIcon className="h-4 w-4" data-id="element-1298" data-mp="2m7rhliskv2xv" />,
              onSelect: () => navigate(`/app/formularios/${form.id}`)
            }, {
              label: 'Vista previa',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-1299" data-mp="rvbeuryzq6xr" />,
              onSelect: () => window.open(`/formulario/${form.slug}`, '_blank')
            }, {
              label: 'Copiar enlace',
              // Copia fiable con confirmación visible.
              icon: <LinkIcon className="h-4 w-4" data-id="element-1300" data-mp="pc6mcpvb14zg" />,
              onSelect: async () => {
                const ok = await copyToClipboard(formUrl(form.slug));
                if (ok) toast.success('Enlace copiado');else toast.error('No se ha podido copiar el enlace');
              }
            }, {
              label: 'Duplicar',
              icon: <CopyIcon className="h-4 w-4" data-id="element-1301" data-mp="5mmg9n6lqav4" />,
              onSelect: () => duplicateForm(form.id)
            }, {
              label: form.status === 'activo' ? 'Desactivar' : 'Activar',
              icon: <PowerIcon className="h-4 w-4" data-id="element-1302" data-mp="fjivebr09v8n" />,
              onSelect: () => setFormStatus(form.id, form.status === 'activo' ? 'inactivo' : 'activo')
            }, {
              label: 'Eliminar',
              icon: <Trash2Icon className="h-4 w-4" data-id="element-1303" data-mp="3j1j2zpsnfylq" />,
              destructive: true,
              onSelect: () => setToDelete(form.id)
            }]} data-id="element-1296" data-mp="1ygm3b82acipo" />
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm" data-id="element-1304" data-mp="3gg1kcn91xfza">
                  <div data-id="element-1305" data-mp="3ge8m9wci842w">
                    <dt className="text-xs text-muted-foreground" data-id="element-1306" data-mp="3bhhy0lqjq26e">Preguntas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1307" data-mp="3bfdxmdruvlav">
                      {form.questions.length}
                    </dd>
                  </div>
                  <div data-id="element-1308" data-mp="31i97y3ysx7pb">
                    <dt className="text-xs text-muted-foreground" data-id="element-1309" data-mp="31l569tek9yea">Respuestas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1310" data-mp="2xj0cmuo451rn">{responses}</dd>
                  </div>
                  <div data-id="element-1311" data-mp="2spfp5wk5bq6p">
                    <dt className="text-xs text-muted-foreground" data-id="element-1312" data-mp="2sow5aaaxqrq1">Creado</dt>
                    <dd className="text-muted-foreground" data-id="element-1313" data-mp="2nrxbxtz3iosw">
                      {formatDate(form.createdAt)}
                    </dd>
                  </div>
                  <div data-id="element-1314" data-mp="2dtpyhl40movn">
                    <dt className="text-xs text-muted-foreground" data-id="element-1315" data-mp="2ds6lc3dnoty3">Modificado</dt>
                    <dd className="text-muted-foreground" data-id="element-1316" data-mp="29sxp8rv58jzd">
                      {formatDate(form.updatedAt)}
                    </dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-4" data-id="element-1317" data-mp="246dmm68glmsw">
                  <Tag data-id="element-1318" data-mp="1q6mcigl2cqk6">
                    {form.status === 'activo' ? 'Activo' : form.status === 'borrador' ? 'Borrador' : 'Inactivo'}
                  </Tag>
                  {linked.map(landing => <Tag key={landing.id} data-id="element-1319" data-mp="2iuqp7m21y3wd">{landing.name}</Tag>)}
                  {linked.length === 0 && <span className="text-xs text-muted-foreground" data-id="element-1320" data-mp="2ix4h5m1fqzzp">
                      Sin landing asociada
                    </span>}
                </div>
              </li>;
      })}
        </ul>}

      <Modal open={creating} onClose={() => setCreating(false)} title="Nuevo formulario" footer={<div className="flex justify-end gap-2" data-id="element-1322" data-mp="2e1xfejrq85df">
            <Button variant="ghost" onClick={() => setCreating(false)} data-id="element-1323" data-mp="31tckulesv2sw">
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!newName.trim() || !ready} data-id="element-1324" data-mp="36l4ex5x5zqns">
              {ready ? 'Crear' : 'Cargando…'}
            </Button>
          </div>} data-id="element-1321" data-mp="2934d7nhw40sk">
        {!ready && <p role="status" className="mb-4 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground" data-id="element-1325" data-mp="3bwi17r5v9ybe">
            Preparando tu espacio de trabajo…
          </p>}
        <Input id="nombre-form" label="Nombre del formulario" placeholder="Solicitud de presupuesto" value={newName} onChange={e => setNewName(e.target.value)} data-id="element-1326" data-mp="3kq4r50ne87co" />
      </Modal>

      <ConfirmDialog open={Boolean(toDelete)} title="Eliminar formulario" description="Las landings asociadas se quedarán sin formulario. Esta acción no se puede deshacer." onCancel={() => setToDelete(null)} onConfirm={async () => {
      if (toDelete) await deleteForm(toDelete);
      setToDelete(null);
    }} data-id="element-1327" data-mp="2x2d6n9rp8o0b" />
    </>;
}